import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Lock, Eye, EyeOff, ArrowLeft, CheckCircle } from "lucide-react";
import { useFormValidation } from "../../../../hooks";
import { authApi } from "../../../../api/authApi";
import { useB2BTranslation } from "../../B2BShared/hooks/useB2BTranslation";
import "./B2BResetPassword.css";

interface FormErrors {
  general?: string;
  password?: string;
  confirmPassword?: string;
  [key: string]: string | undefined;
}

const B2BResetPassword: React.FC = () => {
  const { tAuth, tCommon } = useB2BTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [token, setToken] = useState<string>("");

  const {
    formData,
    errors,
    isLoading,
    setIsLoading,
    handleInputChange,
    setGeneralError,
    setFieldError,
  } = useFormValidation({
    password: "",
    confirmPassword: "",
  });

  const typedErrors = errors as FormErrors;

  useEffect(() => {
    const resetToken = searchParams.get("token");
    if (!resetToken) {
      navigate("/gcb/forgot-password");
      return;
    }
    setToken(resetToken);
  }, [searchParams, navigate]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();

    // Manual validation since validateForm doesn't accept arguments
    const newErrors: FormErrors = {};
    
    if (!formData.password || formData.password.trim().length === 0) {
      newErrors.password = tAuth("passwordRequired");
    } else if (formData.password.length < 8) {
      newErrors.password = tAuth("passwordMinLength");
    }
    
    if (!formData.confirmPassword || formData.confirmPassword.trim().length === 0) {
      newErrors.confirmPassword = tAuth("passwordRequired");
    } else if (formData.confirmPassword !== formData.password) {
      newErrors.confirmPassword = tAuth("passwordMismatch");
    }

    if (Object.keys(newErrors).length > 0) {
      // Set errors for each field
      if (newErrors.password) {
        setFieldError("password", newErrors.password);
      }
      if (newErrors.confirmPassword) {
        setFieldError("confirmPassword", newErrors.confirmPassword);
      }
      return;
    }

    setIsLoading(true);

    try {
      await authApi.resetPassword(token, formData.password);
      setIsSuccess(true);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : tAuth("resetPasswordError");
      setGeneralError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = (): void => setShowPassword(prev => !prev);
  const toggleConfirmPasswordVisibility = (): void =>
    setShowConfirmPassword(prev => !prev);

  if (isSuccess) {
    return (
      <div className="b2b-reset-password-container">
        <div className="b2b-reset-password-card">
          <div className="b2b-success-message">
            <CheckCircle className="b2b-success-icon" />
            <h1>{tAuth("resetPasswordSuccessTitle")}</h1>
            <p>{tAuth("resetPasswordSuccessMessage")}</p>
          </div>

          <div className="b2b-reset-password-actions">
            <Link
              to="/gcb/login"
              className="b2b-login-btn"
            >
              {tAuth("signIn")}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="b2b-reset-password-container">
      <div className="b2b-reset-password-card">
        <div className="b2b-reset-password-header">
          <Link
            to="/gcb/forgot-password"
            className="b2b-back-button"
          >
            <ArrowLeft />
            <span>{tAuth("backToLogin")}</span>
          </Link>

          <h1 className="b2b-reset-password-title">
            {tAuth("resetPassword")}
          </h1>
          <p className="b2b-reset-password-subtitle">
            {tAuth("resetPasswordDescription")}
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="b2b-reset-password-form"
        >
          {typedErrors.general && (
            <div className="b2b-error-alert">{typedErrors.general}</div>
          )}

          {/* New Password Field */}
          <div className="b2b-form-group">
            <label className="b2b-field-label">{tAuth("newPassword")}</label>
            <div className="b2b-input-container">
              <Lock className="b2b-input-icon" />
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder={tAuth("newPasswordPlaceholder")}
                className={`b2b-input ${typedErrors.password ? "b2b-input-error" : ""}`}
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={togglePasswordVisibility}
                className="b2b-password-toggle"
                disabled={isLoading}
              >
                {showPassword ? <EyeOff /> : <Eye />}
              </button>
            </div>
            {typedErrors.password && (
              <span className="b2b-error-message">{typedErrors.password}</span>
            )}
          </div>

          {/* Confirm Password Field */}
          <div className="b2b-form-group">
            <label className="b2b-field-label">
              {tAuth("confirmPassword")}
            </label>
            <div className="b2b-input-container">
              <Lock className="b2b-input-icon" />
              <input
                type={showConfirmPassword ? "text" : "password"}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                placeholder={tAuth("confirmPasswordPlaceholder")}
                className={`b2b-input ${typedErrors.confirmPassword ? "b2b-input-error" : ""}`}
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={toggleConfirmPasswordVisibility}
                className="b2b-password-toggle"
                disabled={isLoading}
              >
                {showConfirmPassword ? <EyeOff /> : <Eye />}
              </button>
            </div>
            {typedErrors.confirmPassword && (
              <span className="b2b-error-message">
                {typedErrors.confirmPassword}
              </span>
            )}
          </div>

          <div className="password-requirements">
            <small>
              {tAuth("passwordRequirementsTitle")}
              <ul>
                <li>{tAuth("passwordRequirementMinLength")}</li>
                <li>{tAuth("passwordRequirementUppercase")}</li>
                <li>{tAuth("passwordRequirementLowercase")}</li>
                <li>{tAuth("passwordRequirementNumber")}</li>
                <li>{tAuth("passwordRequirementSpecial")}</li>
              </ul>
            </small>
          </div>

          <button
            type="submit"
            className="b2b-reset-password-button"
            disabled={isLoading}
          >
            {isLoading ? tCommon("loading") : tAuth("resetPassword")}
          </button>

          <div className="b2b-reset-password-footer">
            <span>{tAuth("rememberPassword")} </span>
            <Link
              to="/gcb/login"
              className="b2b-login-link"
            >
              {tAuth("signIn")}
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default B2BResetPassword;

